import { z } from 'zod'
import { safeCssColor } from './css-safe'

// Shared color palette + schema for the reused explorer `PropertyCard` and its
// CSS-variable contract. Hoisted out of `widgets/explorer/config.ts` (#742) so
// the widgets that reuse the card (explorer, related-properties, property-map)
// share one source instead of importing from inside another widget's config.

// #904 defense-in-depth: these values are interpolated raw into a <style> via
// dangerouslySetInnerHTML, so an unsafe string is a stored-XSS vector. Coerce
// any value that is not a syntactically safe CSS color back to the field
// default on parse (matching the lenientHex pattern used elsewhere) — rgb()/
// rgba()/hsl()/hex/named/var() all pass; injection payloads fall back. The
// emission layer (useExplorerCss) sanitizes again, so already-stored values
// and direct-API writes are covered regardless of this parse.
const colorField = (def: string, desc: string) =>
  z
    .preprocess((v) => {
      if (typeof v !== 'string') return v // undefined → z default(def) applies
      const t = v.trim()
      return t !== '' && safeCssColor(t) !== '' ? t : def
    }, z.string().default(def))
    .describe(desc)

export const explorerLightColors = {
  accent: '#f59e0c',
  foreground: '#333333',
  mutedForeground: '#999999',
  background: '#e8e9ed',
  cardBackground: '#ffffff',
  cardForeground: '#666666',
  inputBackground: '#DDDFE0',
  inputForeground: '#000000',
  inputPlaceholder: '#000000',
  borderColor: '#e5e5e5',
  buttonBackground: '#2c3e3e',
  buttonForeground: '#ffffff',
}

export const explorerDarkColors = {
  accent: '#f59e0c',
  foreground: '#e0e0e0',
  mutedForeground: '#777777',
  background: '#1e1e1e',
  cardBackground: '#1a1a1a',
  cardForeground: '#cccccc',
  inputBackground: '#2a2a2a',
  inputForeground: '#e0e0e0',
  inputPlaceholder: '#999999',
  borderColor: '#444444',
  buttonBackground: '#444444',
  buttonForeground: '#e0e0e0',
}

export const explorerColorSchema = (defaults: typeof explorerLightColors) => z.object({
  accent: colorField(defaults.accent, 'Accent color (search button, highlights)'),
  foreground: colorField(defaults.foreground, 'Primary text color'),
  mutedForeground: colorField(defaults.mutedForeground, 'Secondary/muted text color'),
  background: colorField(defaults.background, 'Filter bar background'),
  cardBackground: colorField(defaults.cardBackground, 'Property card background'),
  cardForeground: colorField(defaults.cardForeground, 'Property card text color'),
  inputBackground: colorField(defaults.inputBackground, 'Form input/trigger background'),
  inputForeground: colorField(defaults.inputForeground, 'Form input text color'),
  inputPlaceholder: colorField(defaults.inputPlaceholder, 'Input placeholder color'),
  borderColor: colorField(defaults.borderColor, 'Borders and dividers'),
  buttonBackground: colorField(defaults.buttonBackground, 'Secondary button background'),
  buttonForeground: colorField(defaults.buttonForeground, 'Secondary button text color'),
})
